import { loadImage } from '../components/image-crop';

const encode = (canvas: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));

export const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/**
 * A picture file made ready to upload: at most `maxSide` pixels on its longest side, as WebP so a transparent
 * background stays transparent (browsers that can't write WebP give a PNG). A picture still over `maxBytes` becomes a
 * JPEG on white. Redrawing it also drops the file's metadata, such as where a photo was taken.
 */
export async function resizeImage(file: File, { maxSide, maxBytes, quality = 0.88 }: { maxSide: number; maxBytes: number; quality?: number }) {
  const source = await loadImage(file).catch(() => {
    throw new Error(`${file.name || 'That file'} couldn’t be opened. Choose a PNG, JPEG, or WebP picture.`);
  });
  try {
    const { naturalWidth, naturalHeight } = source.image;
    const scale = Math.min(1, maxSide / Math.max(naturalWidth, naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(naturalHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('This browser couldn’t prepare the picture. Try another browser.');
    context.imageSmoothingQuality = 'high';
    context.drawImage(source.image, 0, 0, canvas.width, canvas.height);

    let blob = await encode(canvas, 'image/webp', quality);
    if (!blob || blob.size > maxBytes) {
      const flat = document.createElement('canvas');
      flat.width = canvas.width;
      flat.height = canvas.height;
      const flatContext = flat.getContext('2d')!;
      flatContext.fillStyle = '#ffffff';
      flatContext.fillRect(0, 0, flat.width, flat.height);
      flatContext.drawImage(canvas, 0, 0);
      blob = await encode(flat, 'image/jpeg', 0.82);
    }
    if (!blob || blob.size > maxBytes) throw new Error(`${file.name || 'That picture'} is too large to use. Try a smaller one.`);
    return { blob, width: canvas.width, height: canvas.height };
  } finally {
    URL.revokeObjectURL(source.url);
  }
}
